import {
	useLayoutEffect,
	useRef,
	useState,
	type KeyboardEvent,
	type ReactNode,
	type Ref,
} from "react";
import { DropdownChevron, ExpandIn } from "@/components/common/Disclosure";
import { Icon } from "@/components/common/Icon";
import { HITLSubmittingNote } from "@/components/hitl/HITLSubmittingNote";

const HEADER_BUTTON_CLASS =
	"flex items-center justify-center rounded-3 p-1 text-fg-3 transition-colors enabled:hover:bg-surface-2 enabled:hover:text-fg-1 disabled:opacity-40";

interface ChoiceCardProps {
	title: ReactNode;
	context?: ReactNode;
	/** 指定時のみ上端に進捗バーと「質問 n / m」を出す。 */
	progress?: { current: number; total: number };
	/** 指定時のみ右上にスキップ (×) を出す。 */
	onSkip?: () => void;
	/** 選択肢の上に並べる判断材料。 */
	details?: ReactNode;
	hint?: ReactNode;
	actions?: ReactNode;
	children?: ReactNode;
	rootRef?: Ref<HTMLDivElement>;
	onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void;
	testId?: string;
	/** 回答の送信中。中の操作をすべて押せなくし、ヒントの位置に送信中と出す。 */
	busy?: boolean;
}

/**
 * HITL の選択カードの器。選択肢と各スロットの配置だけを担う。
 * 回答前にチャットを読み返せるよう、タイトル行だけ残して折りたためる。
 */
export function ChoiceCard({
	title,
	context,
	progress,
	onSkip,
	details,
	hint,
	actions,
	children,
	rootRef,
	onKeyDown,
	testId,
	busy = false,
}: ChoiceCardProps) {
	const fieldsetRef = useRef<HTMLFieldSetElement>(null);
	// 最初から開いている間は展開のアニメーションを出さない。
	const [expansion, setExpansion] = useState<"initial" | "collapsed" | "expanded">("initial");
	const collapsed = expansion === "collapsed";

	// 押せなくした要素からはフォーカスが外れ、カードのキー操作が効かなくなる。送信に失敗して
	// 戻ったときもキー操作できるよう、フォーカスをカード本体へ移しておく。
	useLayoutEffect(() => {
		const fieldset = fieldsetRef.current;
		if (!busy || !fieldset) return;
		const active = document.activeElement;
		if (active === document.body || fieldset.contains(active)) {
			fieldset.parentElement?.focus();
		}
	}, [busy]);

	// 畳んで消える選択肢や、開き直した後の Enter で再び畳んでしまうトグルにフォーカスを残さない。
	function toggleCollapsed() {
		fieldsetRef.current?.parentElement?.focus();
		setExpansion(collapsed ? "expanded" : "collapsed");
	}

	// 折りたたみ中は、見えない選択肢を Enter で送ったり Esc でスキップしたりしないよう止める。
	const acceptsKeys = !busy && !collapsed;

	return (
		<div
			ref={rootRef}
			data-testid={testId}
			tabIndex={-1}
			// 下の縁が入力欄の影 (shadow-composer-boundary) に隠れないよう、入力欄より手前に置く。
			className="relative z-1 rounded-4 bg-surface-1 focus:outline-none"
			style={{ boxShadow: "var(--shadow-card), var(--shadow-composer-boundary)" }}
			onKeyDown={acceptsKeys ? onKeyDown : undefined}
		>
			{progress && (
				// 枠いっぱいの選択肢のフォーカス枠まで切らないよう、角丸で切るのは進捗バーだけにする。
				<div className="pointer-events-none absolute inset-0 overflow-hidden rounded-4">
					<div className="h-0.5 w-full bg-surface-2">
						<div
							className="h-full transition-all duration-300 ease-out"
							style={{
								width: `${(progress.current / progress.total) * 100}%`,
								backgroundColor: "var(--accent-solid)",
							}}
						/>
					</div>
				</div>
			)}

			{/* fieldset の disabled で、中のボタン・入力をまとめて押せなくする。 */}
			<fieldset ref={fieldsetRef} disabled={busy} className="m-0 min-w-0 border-0 px-4 pb-3 pt-3">
				<div className="flex items-start justify-between gap-3">
					<div className="min-w-0 flex-1">
						{progress && (
							<p className="mb-1 text-caption font-medium text-fg-3">
								質問 {progress.current} / {progress.total}
							</p>
						)}
						<h3 className="wrap-anywhere text-ui font-semibold tracking-tight text-fg-1">
							{title}
						</h3>
						{context && !collapsed && (
							<p className="mt-0.5 wrap-anywhere text-caption text-fg-3">{context}</p>
						)}
					</div>
					<div className="flex shrink-0 items-center gap-0.5">
						{collapsed && busy && <HITLSubmittingNote />}
						<button
							type="button"
							data-testid="choice-card-collapse-toggle"
							onClick={toggleCollapsed}
							className={HEADER_BUTTON_CLASS}
						>
							<DropdownChevron open={collapsed} size={14} color="currentColor" />
						</button>
						{onSkip && (
							<button
								type="button"
								title="スキップ"
								onClick={onSkip}
								className={HEADER_BUTTON_CLASS}
							>
								<Icon name="x" size={14} />
							</button>
						)}
					</div>
				</div>

				{!collapsed && (
					<ExpandIn animate={expansion === "expanded"}>
						<div className="pt-3">
							{/* 長い判断材料で選択肢やボタンが画面の外へ出ないよう、この欄だけ高さを抑えてスクロールさせる。
							    端のフォーカスの枠が切れないよう内側に余白を取り、同じ幅だけ外へ広げて中身の位置は変えない。 */}
							{details && (
								<div className="-mx-1 -mt-1 mb-2 grid max-h-[40vh] gap-1.5 overflow-y-auto p-1 text-caption">
									{details}
								</div>
							)}

							{children}

							<div className="mt-3 flex items-center justify-between gap-3">
								{busy ? (
									<HITLSubmittingNote />
								) : (
									<p className="min-w-0 text-caption text-fg-3">{hint}</p>
								)}
								{actions && <div className="flex shrink-0 items-center gap-2">{actions}</div>}
							</div>
						</div>
					</ExpandIn>
				)}
			</fieldset>
		</div>
	);
}
